/**
 * Sistema de feedback simplificado que imita o comportamento do toast
 * Mensagens são persistidas e exibidas após redirecionamento
 */

import { LoadingType } from "@/store/globalLoading/globalLoadingSlice";

export interface FeedbackAction {
  label: string;
  url: string;
}

interface FeedbackMessage {
  message: string;
  type: LoadingType;
  timestamp: number;
  action?: FeedbackAction;
}

const STORAGE_KEY = "app_feedback";
const MAX_AGE = 5000; // 5 segundos

/**
 * Mostra uma mensagem de sucesso (equivalente ao toast.success)
 */
export const success = (message: string, action?: FeedbackAction): void => {
  storeFeedback(message, "success", action);
};

/**
 * Mostra uma mensagem de erro (equivalente ao toast.error)
 */
export const error = (message: string, action?: FeedbackAction): void => {
  storeFeedback(message, "error", action);
};

/**
 * Mostra uma mensagem de warning (equivalente ao toast.warning)
 */
export const warning = (message: string, action?: FeedbackAction): void => {
  storeFeedback(message, "warning", action);
};

/**
 * Armazena feedback para exibição
 */
const storeFeedback = (
  message: string,
  type: LoadingType,
  action?: FeedbackAction
): void => {
  const feedback: FeedbackMessage = {
    message,
    type,
    timestamp: Date.now(),
    action,
  };

  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(feedback));
  }
};

/**
 * Recupera e remove a mensagem armazenada
 */
export const getFeedback = (): FeedbackMessage | null => {
  if (typeof window === "undefined") return null;

  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;

  try {
    const feedback: FeedbackMessage = JSON.parse(stored);

    // Verifica se não expirou
    const age = Date.now() - feedback.timestamp;
    if (age > MAX_AGE) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    // Remove após ler (uso único)
    localStorage.removeItem(STORAGE_KEY);
    return feedback;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

/**
 * Limpa qualquer feedback pendente
 */
export const clear = (): void => {
  if (typeof window !== "undefined") {
    localStorage.removeItem(STORAGE_KEY);
  }
};

/**
 * Exibe um diálogo de confirmação nativo do navegador
 */
export const confirm = async (
  message: string,
  detail?: string
): Promise<boolean> => {
  const fullMessage = detail ? `${message}\n\n${detail}` : message;
  return window.confirm(fullMessage);
};

// Export default para uso como: feedback.success("Mensagem")
export default {
  success,
  error,
  warning,
  clear,
  confirm,
};
